const INTERMEDIATE_DEFAULTS = {
  giftsDisplayMode: "none",
  linkRedirectsDisplayMode: "none",
  showGifts: false,
  showLinkRedirects: false,
  recapPosition: "top",
};

const THANK_YOU_DEFAULTS = {
  giftsDisplayMode: "last",
  linkRedirectsDisplayMode: "all",
  showGifts: true,
  showLinkRedirects: true,
  recapPosition: "top",
  richContent: "",
};

const DISPLAY_MODES = new Set(["last", "all", "none"]);

function resolveDisplayMode(mode, showFlag, whenTrue) {
  if (DISPLAY_MODES.has(mode)) {
    return mode;
  }
  if (showFlag === false) {
    return "none";
  }
  return whenTrue;
}

function mergeSettings(defaults, overrides) {
  if (!overrides) {
    return { ...defaults };
  }
  return {
    ...defaults,
    showGifts:
      overrides.showGifts !== undefined
        ? overrides.showGifts
        : defaults.showGifts,
    showLinkRedirects:
      overrides.showLinkRedirects !== undefined
        ? overrides.showLinkRedirects
        : defaults.showLinkRedirects,
    recapPosition:
      overrides.recapPosition === "bottom" ? "bottom" : defaults.recapPosition,
  };
}

function mergeExtraSellSettings(overrides) {
  const merged = mergeSettings(INTERMEDIATE_DEFAULTS, overrides);
  const giftsDisplayMode = resolveDisplayMode(
    overrides?.giftsDisplayMode,
    merged.showGifts,
    "last",
  );
  const linkRedirectsDisplayMode = resolveDisplayMode(
    overrides?.linkRedirectsDisplayMode,
    merged.showLinkRedirects,
    "last",
  );
  return {
    ...merged,
    giftsDisplayMode,
    linkRedirectsDisplayMode,
    showGifts: giftsDisplayMode !== "none",
    showLinkRedirects: linkRedirectsDisplayMode !== "none",
  };
}

function mergeThankYouSettings(overrides) {
  const merged = mergeSettings(THANK_YOU_DEFAULTS, overrides);
  const giftsDisplayMode = resolveDisplayMode(
    overrides?.giftsDisplayMode,
    merged.showGifts,
    "last",
  );
  const linkRedirectsDisplayMode = resolveDisplayMode(
    overrides?.linkRedirectsDisplayMode,
    merged.showLinkRedirects,
    "all",
  );
  return {
    ...merged,
    giftsDisplayMode,
    linkRedirectsDisplayMode,
    showGifts: giftsDisplayMode !== "none",
    showLinkRedirects: linkRedirectsDisplayMode !== "none",
    richContent:
      typeof overrides?.richContent === "string" ? overrides.richContent : "",
  };
}

/**
 * Resolve confirmation display settings for the active confirmation context.
 */
export function resolveActiveConfirmationSettings(context = {}) {
  const settings = window.productDetails?.confirmationSettings;
  const {
    hasExtrasellOffers,
    stepType,
    step,
    parentChoiceId,
    orderPageItemId,
  } = context;

  if (!hasExtrasellOffers) {
    return mergeThankYouSettings(settings?.thankYou);
  }

  if (stepType === "postUpsells") {
    const upsells = settings?.upsells || {};
    if (orderPageItemId && upsells[orderPageItemId]) {
      return mergeExtraSellSettings(upsells[orderPageItemId]);
    }
    if (parentChoiceId && upsells[parentChoiceId]) {
      return mergeExtraSellSettings(upsells[parentChoiceId]);
    }
    const firstKey = Object.keys(upsells)[0];
    if (firstKey) {
      return mergeExtraSellSettings(upsells[firstKey]);
    }
    return { ...INTERMEDIATE_DEFAULTS };
  }

  if (stepType === "crossSells") {
    const crossSells = settings?.crossSells || {};
    if (orderPageItemId && crossSells[orderPageItemId]) {
      return mergeExtraSellSettings(crossSells[orderPageItemId]);
    }
    const stepKey = String(step ?? "");
    if (crossSells[stepKey]) {
      return mergeExtraSellSettings(crossSells[stepKey]);
    }
    const matchedEntry = Object.values(crossSells).find(
      (entry) => String(entry?.step) === stepKey,
    );
    if (matchedEntry) {
      return mergeExtraSellSettings(matchedEntry);
    }
    return { ...INTERMEDIATE_DEFAULTS };
  }

  return { ...INTERMEDIATE_DEFAULTS };
}
